import React, { useState, useEffect } from 'react';
import config from '../config';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Typography,
  Tooltip,
  Autocomplete
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Check as CheckIcon, Close as CloseIcon, Print as PrintIcon, Refresh as RefreshIcon, Lock as LockIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useConfirm } from '../hooks/useConfirm';
import { deductionsService } from '../services/deductions.service';
import employeesService from '../services/employees.service';
import { branchesService } from '../services/branches.service';
import { companyInfoService, CompanyInfo } from '../services/companyInfo.service';
import { useLanguage } from '../contexts/LanguageContext';
import { useAppSelector } from '../hooks/redux.hooks';
import { Deduction } from '../types/deductions.types';
import { Employee } from '../types/employees.types';
import { Branch } from '../types/branches.types';
import DeductionDialog from '../components/dialogs/DeductionDialog';
import AttachmentSection from '../components/common/AttachmentSection';

const DeductionsPage: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const confirm = useConfirm();
  const user = useAppSelector((state) => state.auth.user);
  const permissions = useAppSelector((state) => state.auth.permissions);
  const isSuperAdmin = user?.isSuperAdmin === true;

  const userBranchIds = JSON.parse(localStorage.getItem('userBranchIds') || '[]');
  const canEditDeduction = (branchId: number) => {
    return userBranchIds.length === 0 || userBranchIds.includes(branchId);
  };
  const canApproveDeduction = () => {
    return userBranchIds.length === 0;
  };

  const [deductions, setDeductions] = useState<Deduction[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDeduction, setSelectedDeduction] = useState<Deduction | null>(null);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  
  const [selectedBranch, setSelectedBranch] = useState<number | ''>('');
  const [selectedEmployee, setSelectedEmployee] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const t = {
    title: isRTL ? 'إدارة الخصومات' : 'Deductions Management',
    addNew: isRTL ? 'إضافة خصم' : 'Add Deduction',
    branch: isRTL ? 'الفرع' : 'Branch',
    employee: isRTL ? 'الموظف' : 'Employee',
    status: isRTL ? 'الحالة' : 'Status',
    search: isRTL ? 'بحث' : 'Search',
    clear: isRTL ? 'مسح' : 'Clear',
    print: isRTL ? 'طباعة' : 'Print',
    deductionType: isRTL ? 'نوع الخصم' : 'Deduction Type',
    amount: isRTL ? 'المبلغ' : 'Amount',
    deductionDate: isRTL ? 'تاريخ الخصم' : 'Date',
    reason: isRTL ? 'السبب' : 'Reason',
    reference: isRTL ? 'المرجع' : 'Reference',
    actions: isRTL ? 'الإجراءات' : 'Actions',
    pending: isRTL ? 'معلق' : 'Pending',
    applied: isRTL ? 'مطبق' : 'Applied',
    cancelled: isRTL ? 'ملغي' : 'Cancelled',
    all: isRTL ? 'الكل' : 'All',
    loadError: isRTL ? 'خطأ في تحميل البيانات' : 'Error loading data',
    deleteSuccess: isRTL ? 'تم الحذف بنجاح' : 'Deleted successfully',
    approveSuccess: isRTL ? 'تمت الموافقة بنجاح' : 'Approved successfully',
    confirmDelete: isRTL ? 'هل أنت متأكد من الحذف؟' : 'Are you sure?',
    reportTitle: isRTL ? 'تقرير الخصومات' : 'Deductions Report',
    totalAmount: isRTL ? 'إجمالي المبلغ' : 'Total Amount',
    count: isRTL ? 'العدد' : 'Count',
    noData: isRTL ? 'لا توجد بيانات' : 'No data found',
    recurring: isRTL ? 'متكرر' : 'Recurring',
    installment: isRTL ? 'قسط' : 'Installment'
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [deductionsData, employeesData, branchesData, companyData] = await Promise.all([
        deductionsService.getAll(),
        employeesService.getAll(),
        branchesService.getAll(),
        companyInfoService.getCompanyInfo().catch(() => null)
      ]);
      
      const filteredDeductions = deductionsData.filter((deduction: Deduction) => 
        canEditDeduction(deduction.branchId)
      );
      
      setDeductions(filteredDeductions);
      setEmployees(employeesData.data || employeesData);
      setBranches(branchesData);
      setCompanyInfo(companyData);
    } catch (error) {
      toast.error(t.loadError);
    }
  };

  const handleSearch = async () => {
    try {
      const data = await deductionsService.getAll({
        branchId: selectedBranch || undefined,
        employeeId: selectedEmployee || undefined,
        status: statusFilter || undefined
      });
      
      const filteredData = data.filter((deduction: Deduction) => 
        canEditDeduction(deduction.branchId)
      );
      
      setDeductions(filteredData);
    } catch (error) {
      toast.error(t.loadError);
    }
  };

  const handleDelete = async (id: number) => {
    if (!(await confirm({ message: t.confirmDelete, severity: 'error' }))) return;
    try {
      await deductionsService.delete(id);
      toast.success(t.deleteSuccess);
      loadData();
    } catch (error) {
      toast.error(t.loadError);
    }
  };

  const handleAcknowledge = async (id: number) => {
    try {
      await deductionsService.acknowledge(id);
      toast.success(isRTL ? 'تمت الموافقة الأولية' : 'Acknowledged');
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error');
    }
  };

  const handleApprove = async (id: number, status: 'Approved' | 'Rejected') => {
    try {
      await deductionsService.approve({ deductionId: id, status });
      toast.success(t.approveSuccess);
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t.loadError);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Applied': return 'success';
      case 'Approved': return 'success';
      case 'Acknowledged': return 'warning';
      case 'Cancelled': return 'error';
      case 'Pending': return 'warning';
      default: return 'default';
    }
  };

  const getDeductionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      Penalty: isRTL ? 'جزاء' : 'Penalty',
      LoanDeduction: isRTL ? 'قرض' : 'Loan',
      Insurance: isRTL ? 'تأمين' : 'Insurance',
      SocialSecurity: isRTL ? 'تأمينات' : 'Social Security',
      Advance: isRTL ? 'سلفة' : 'Advance',
      Damage: isRTL ? 'تلفيات' : 'Damage',
      Other: isRTL ? 'أخرى' : 'Other'
    };
    return labels[type] || type;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      Pending: isRTL ? 'معلق' : 'Pending',
      Acknowledged: isRTL ? 'موافقة أولية' : 'Acknowledged',
      Approved: isRTL ? 'معتمد' : 'Approved',
      Applied: isRTL ? 'مطبق' : 'Applied',
      Cancelled: isRTL ? 'ملغي' : 'Cancelled'
    };
    return labels[status] || status;
  };

  const getStatusPrintColor = (status: string) => {
    switch (status) {
      case 'Applied': return '#4caf50';
      case 'Cancelled': return '#f44336';
      case 'Pending': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  const handlePrint = () => {
    if (deductions.length === 0) {
      toast.info(t.noData);
      return;
    }

    const totalAmount = deductions.reduce((sum, d) => sum + d.amount, 0);
    const branchName = selectedBranch ? branches.find(b => b.branchId === selectedBranch) : null;
    const employeeName = selectedEmployee ? employees.find(e => e.employeeId === selectedEmployee) : null;

    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) return;

    printWindow.document.write(`
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <title>${t.reportTitle}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; direction: ${isRTL ? 'rtl' : 'ltr'}; color: #333; font-size: 12px; }
          .header { text-align: center; border-bottom: 3px solid #ff5722; padding-bottom: 15px; margin-bottom: 15px; }
          .header img { max-height: 60px; margin-bottom: 8px; }
          .header h2 { color: #ff5722; margin: 5px 0; }
          .header .subtitle { font-size: 11px; color: #666; }
          .report-title { text-align: center; margin-bottom: 15px; }
          .report-title h3 { color: #ff5722; font-size: 20px; }
          .filters-box { border: 1px solid #ddd; border-radius: 6px; padding: 10px 15px; margin-bottom: 15px; background: #fbe9e7; }
          .filters-box span { font-size: 12px; margin-${isRTL ? 'left' : 'right'}: 15px; }
          .filters-box strong { color: #ff5722; }
          .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-bottom: 15px; }
          .summary-card { border: 2px solid #ff5722; border-radius: 8px; padding: 12px; text-align: center; }
          .summary-card .value { font-size: 22px; font-weight: bold; color: #ff5722; }
          .summary-card .label { font-size: 11px; color: #666; margin-top: 3px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
          thead th { background: #ff5722; color: white; padding: 8px 6px; font-size: 11px; text-align: ${isRTL ? 'right' : 'left'}; }
          thead th.center { text-align: center; }
          thead th.amount { text-align: ${isRTL ? 'left' : 'right'}; }
          tbody td { padding: 6px; border-bottom: 1px solid #eee; font-size: 11px; }
          tbody td.center { text-align: center; }
          tbody td.amount { text-align: ${isRTL ? 'left' : 'right'}; font-weight: bold; }
          tbody tr:nth-child(even) { background: #fbe9e7; }
          .status-badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: bold; color: white; }
          .total-row td { background: #ff5722; color: white; font-weight: bold; font-size: 12px; padding: 8px 6px; }
          .employee-name { font-weight: bold; }
          .employee-code { font-size: 10px; color: #888; }
          .recurring-badge { display: inline-block; padding: 1px 6px; border-radius: 8px; font-size: 9px; background: #2196f3; color: white; margin-${isRTL ? 'right' : 'left'}: 4px; }
          .signatures { display: flex; justify-content: space-between; margin-top: 50px; }
          .sig-block { text-align: center; width: 30%; }
          .sig-line { border-top: 1px solid #333; padding-top: 8px; margin-top: 50px; font-weight: bold; font-size: 12px; }
          .print-date { text-align: ${isRTL ? 'left' : 'right'}; font-size: 10px; color: #999; margin-bottom: 10px; }
          @media print { @page { size: A4 landscape; margin: 0.8cm; } }
        </style>
      </head>
      <body>
        <div class="print-date">${isRTL ? 'تاريخ الطباعة:' : 'Print Date:'} ${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div>

        ${companyInfo ? `
        <div class="header">
          ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" onerror="this.style.display='none'" />` : ''}
          <h2>${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
          <div class="subtitle">
            ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
          </div>
        </div>
        ` : ''}

        <div class="report-title">
          <h3>${t.reportTitle}</h3>
        </div>

        ${(selectedBranch || selectedEmployee || statusFilter) ? `
        <div class="filters-box">
          ${branchName ? `<span><strong>${t.branch}:</strong> ${isRTL ? branchName.branchNameAr : branchName.branchName}</span>` : ''}
          ${employeeName ? `<span><strong>${t.employee}:</strong> ${isRTL ? employeeName.fullNameAr : employeeName.fullName}</span>` : ''}
          ${statusFilter ? `<span><strong>${t.status}:</strong> ${getStatusLabel(statusFilter)}</span>` : ''}
        </div>
        ` : ''}

        <div class="summary-grid">
          <div class="summary-card">
            <div class="value">${deductions.length}</div>
            <div class="label">${t.count}</div>
          </div>
          <div class="summary-card">
            <div class="value">${totalAmount.toFixed(3)}</div>
            <div class="label">${t.totalAmount} (KD)</div>
          </div>
          <div class="summary-card">
            <div class="value">${deductions.filter(d => d.isRecurring).length}</div>
            <div class="label">${t.recurring}</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>${t.employee}</th>
              <th>${t.branch}</th>
              <th>${t.deductionType}</th>
              <th class="amount">${t.amount}</th>
              <th>${t.deductionDate}</th>
              <th>${t.reason}</th>
              <th>${t.reference}</th>
              <th class="center">${t.status}</th>
            </tr>
          </thead>
          <tbody>
            ${deductions.map((d, idx) => `
            <tr>
              <td>${idx + 1}</td>
              <td>
                <span class="employee-name">${isRTL ? d.employeeNameAr : d.employeeName}</span>
                <br/><span class="employee-code">${d.employeeCode}</span>
              </td>
              <td>${d.branchName || '-'}</td>
              <td>
                ${getDeductionTypeLabel(d.deductionType)}
                ${d.isRecurring ? `<span class="recurring-badge">${t.recurring} ${d.installmentNumber || ''}/${d.totalInstallments || ''}</span>` : ''}
              </td>
              <td class="amount">${d.amount.toFixed(3)}</td>
              <td>${new Date(d.deductionDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</td>
              <td>${d.reason || '-'}</td>
              <td>${d.referenceNumber || '-'}</td>
              <td class="center"><span class="status-badge" style="background:${getStatusPrintColor(d.status)}">${getStatusLabel(d.status)}</span></td>
            </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="4" style="text-align: ${isRTL ? 'left' : 'right'};">${isRTL ? 'الإجمالي' : 'Total'}</td>
              <td class="amount">${totalAmount.toFixed(3)} KD</td>
              <td colspan="4"></td>
            </tr>
          </tbody>
        </table>

        <div class="signatures">
          <div class="sig-block"><div class="sig-line">${isRTL ? 'مدير الموارد البشرية' : 'HR Manager'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المدير المالي' : 'Financial Manager'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المدير العام' : 'General Manager'}</div></div>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      // printWindow stays open for user review
    }, 500);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t.title}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title={isRTL ? 'تحديث' : 'Refresh'}>
            <IconButton onClick={loadData} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} color="secondary">{t.print}</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setSelectedDeduction(null); setDialogOpen(true); }}>{t.addNew}</Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <Autocomplete
            options={branches}
            getOptionLabel={(option) => isRTL ? option.branchNameAr : option.branchName}
            value={branches.find(b => b.branchId === selectedBranch) || null}
            onChange={(_, newValue) => setSelectedBranch(newValue?.branchId || '')}
            renderInput={(params) => <TextField {...params} label={t.branch} size="small" />}
            size="small"
          />
          <Autocomplete
            options={employees}
            getOptionLabel={(option) => `${option.employeeCode} - ${isRTL ? option.fullNameAr : option.fullName}`}
            value={employees.find(e => e.employeeId === selectedEmployee) || null}
            onChange={(_, newValue) => setSelectedEmployee(newValue?.employeeId || '')}
            renderInput={(params) => <TextField {...params} label={t.employee} size="small" />}
            size="small"
          />
          <TextField select label={t.status} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} size="small">
            <MenuItem value="">{t.all}</MenuItem>
            <MenuItem value="Pending">{t.pending}</MenuItem>
            <MenuItem value="Applied">{t.applied}</MenuItem>
            <MenuItem value="Cancelled">{t.cancelled}</MenuItem>
          </TextField>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={handleSearch} fullWidth>{t.search}</Button>
            <Button variant="outlined" onClick={() => { setSelectedBranch(''); setSelectedEmployee(''); setStatusFilter(''); loadData(); }} fullWidth>{t.clear}</Button>
          </Box>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t.employee}</TableCell>
              <TableCell>{t.deductionType}</TableCell>
              <TableCell align="right">{t.amount}</TableCell>
              <TableCell>{t.deductionDate}</TableCell>
              <TableCell>{t.reason}</TableCell>
              <TableCell>{t.reference}</TableCell>
              <TableCell>{t.status}</TableCell>
              <TableCell align="center">{t.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deductions.map((deduction) => {
              const isApplied = deduction.status === 'Applied';
              const isLocked = isApplied && !isSuperAdmin;
              return (
              <TableRow key={deduction.deductionId} sx={isApplied ? { opacity: isSuperAdmin ? 1 : 0.6, bgcolor: 'action.hover' } : undefined}>
                <TableCell>{isRTL ? deduction.employeeNameAr : deduction.employeeName}</TableCell>
                <TableCell>{getDeductionTypeLabel(deduction.deductionType)}</TableCell>
                <TableCell align="right">{deduction.amount.toFixed(3)} KD</TableCell>
                <TableCell>{new Date(deduction.deductionDate).toLocaleDateString('en-GB')}</TableCell>
                <TableCell>{deduction.reason}</TableCell>
                <TableCell>{deduction.referenceNumber}</TableCell>
                <TableCell>
                  {isApplied ? (
                    <Tooltip title={`${isRTL ? 'تم خصمه في كشف رواتب' : 'Applied in payroll'}: ${(deduction as any).appliedInPayrollNumber || ''}`}>
                      <Chip label={`${isRTL ? 'تم الخصم' : 'Applied'} — ${(deduction as any).appliedInPayrollNumber || ''}`} color="info" size="small" variant="filled" icon={<LockIcon />} />
                    </Tooltip>
                  ) : (
                    deduction.status === 'Acknowledged' ? (
                      <Tooltip title={`${isRTL ? 'وافق عليها' : 'Acknowledged by'}: ${(deduction as any).opsManagerName || ''}`}>
                        <Chip label={`${getStatusLabel(deduction.status)} - ${(deduction as any).opsManagerName || ''}`} color="warning" size="small" />
                      </Tooltip>
                    ) : (
                      <Chip label={getStatusLabel(deduction.status)} color={getStatusColor(deduction.status)} size="small" />
                    )
                  )}
                </TableCell>
                <TableCell align="center">
                  {!isLocked && (
                    <>
                      {/* Ops Manager acknowledge — only if no APPROVE permission */}
                      {deduction.status === 'Pending' && permissions?.includes('DEDUCTION_ACKNOWLEDGE') && !permissions?.includes('DEDUCTION_APPROVE') && (
                        <Tooltip title={isRTL ? 'موافقة أولية' : 'Acknowledge'}>
                          <IconButton size="small" color="warning" onClick={() => handleAcknowledge(deduction.deductionId)}><CheckIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                      {/* Final approve — only HR/SuperAdmin */}
                      {(deduction.status === 'Pending' || deduction.status === 'Acknowledged') && permissions?.includes('DEDUCTION_APPROVE') && (
                        <>
                          <Tooltip title={isRTL ? 'اعتماد نهائي' : 'Approve'}>
                            <IconButton size="small" color="success" onClick={() => handleApprove(deduction.deductionId, 'Approved')}><CheckIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title={isRTL ? 'رفض' : 'Reject'}>
                            <IconButton size="small" color="error" onClick={() => handleApprove(deduction.deductionId, 'Rejected')}><CloseIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </>
                      )}
                      {(deduction.status === 'Pending' || deduction.status === 'Acknowledged') && canEditDeduction(deduction.branchId) && (
                        <>
                          <IconButton size="small" onClick={() => { setSelectedDeduction(deduction); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(deduction.deductionId)}><DeleteIcon fontSize="small" /></IconButton>
                        </>
                      )}
                    </>
                  )}
                  {isSuperAdmin && (deduction.status === 'Approved' || isApplied) && (
                    <>
                      <IconButton size="small" onClick={() => { setSelectedDeduction(deduction); setDialogOpen(true); }}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(deduction.deductionId)}><DeleteIcon fontSize="small" /></IconButton>
                    </>
                  )}
                  <AttachmentSection documentType="Deduction" documentId={deduction.deductionId} isRTL={isRTL} />
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <DeductionDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setSelectedDeduction(null); }}
        onSave={loadData}
        deduction={selectedDeduction}
      />
    </Box>
  );
};

export default DeductionsPage;